import { useRef, useState } from 'react';

import ActivitySheet from 'approot/activity-builder/components/shared/sheet/activity-sheet';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';

import { ViewProps } from '../settings/interactive-response.constants';
import { SampleProps } from 'approot/activity-builder/components/sample/activity-sample.constants';
import { MEDIA_SM, useMediaQuery } from 'approot/shared/style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { getComponentSelectorId } from 'approot/activity-builder/components/shared/logic/get-component-selector-id';
import ClassroomAnswerTabs, {
  QUESTION_VIEW,
} from 'approot/activity-builder/classroom/classroom-answer-tabs';
import { getSubActivityInstructionAsText } from 'approot/activity-builder/components/sub-activity/sub-activity.utils';
import ComponentOverlaySample from 'approot/activity-builder/components/shared/component-overlay/component-overlay-sample';
import {
  useInteractiveAudio,
  useInteractiveModal,
} from '../../shared/interactive-response-shared.hooks';
import { InteractiveResponseContainer } from '../../interactive-response.style';
import InteractiveSequenceLargeView from '../interactive-sequence-large-view';
import InteractiveSequenceSmallView from '../interactive-sequence-small-view';
import InteractiveResponseSequencePlayPreview from '../play/interactive-response-sequence-play-preview';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';

const InteractiveResponseViewSample = (props: ViewProps & SampleProps) => {
  const { id, subActivityProperties, properties, banner } = props;

  const closeModalCallbackRef = useRef<Function | undefined>(undefined);

  const { isLesson } = useActivityBuilderPresentContext();
  const isMediaUpSm = useMediaQuery(MEDIA_SM);
  const { isPlaying, startGame, finishGame } = useInteractiveModal();
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const [tab, setTab] = useState<string>(QUESTION_VIEW);

  const { toggleAudio, playAudio, playClick, playStart } =
    useInteractiveAudio();

  return (
    <>
      <SubActivityView subActivityDetails={props.subActivityProperties}>
        <InteractiveResponseContainer id={getComponentSelectorId(id)}>
          <InteractiveResponseSequencePlayPreview {...props} shuffleItems />
          <ComponentOverlaySample
            ariaLabel={`Click to start matching response: ${
              getSubActivityInstructionAsText(
                subActivityProperties?.instructions
              ) || properties.interactiveResponse.title
            }`}
            onClick={() => {
              openModal();
              startGame();
            }}
            data-selector="btn-start"
          />
        </InteractiveResponseContainer>
      </SubActivityView>
      {isVisible && (
        <ActivitySheet
          isVisible
          title={properties.interactiveResponse.title || ''}
          hasBackgroundColor={tab === QUESTION_VIEW}
          onClose={() => {
            if (closeModalCallbackRef.current) {
              closeModalCallbackRef.current();
              closeModalCallbackRef.current = undefined;
            } else {
              closeModal();
            }
          }}
          tabsContent={
            <ClassroomAnswerTabs
              isPublished
              canClearModelledResponse={false}
              includeIndividualAnswers={false}
              includeClassResponse={false}
              tab={tab}
              onSelect={option => setTab(option)}
              componentId={id}
              onCloseActivitySheet={() => {
                if (closeModalCallbackRef.current) {
                  closeModalCallbackRef.current();
                  closeModalCallbackRef.current = undefined;
                } else {
                  closeModal();
                }
              }}
            />
          }
          banner={banner}
        >
          <>
            {isMediaUpSm ? (
              <InteractiveSequenceLargeView
                {...props}
                isPlaying={isPlaying}
                finishGame={finishGame}
                showAnswer={isLesson}
                onAnswerAttempt={() => {}}
                attemptAnswers={undefined}
                setCloseModalCallback={callback => {
                  closeModalCallbackRef.current = callback;
                }}
              />
            ) : (
              <InteractiveSequenceSmallView
                {...props}
                isPlaying={isPlaying}
                finishGame={finishGame}
                showAnswer={isLesson}
                playClick={playClick}
                playStart={playStart}
                playAudio={playAudio}
                toggleAudio={toggleAudio}
                onAnswerAttempt={() => {}}
                attemptAnswers={undefined}
              />
            )}
          </>
        </ActivitySheet>
      )}
    </>
  );
};

export default InteractiveResponseViewSample;
